import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, ALL_PERMISSIONS } from '../../utils/statuses';
import { fmtDt, errMsg } from '../../utils/helpers';
import { Badge, Spinner, Tabs, Modal, ConfirmModal, Field, Select } from '../../components/common/UI';
import { Plus, Edit2, Trash2, Key, Check, Minus, Building2, Users, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_OPTS = Object.entries(ROLES).map(([v, d]) => ({ value: v, label: d.label }));

// Права по умолчанию для дилерского администратора
const DEALER_ADMIN_DEFAULTS = {
  orders:   ['view', 'create', 'edit_content', 'view_prices'],
  repairs:  ['view', 'view_prices'],
  supplies: ['view', 'create', 'edit_content'],
};

export default function AdminPage() {
  const { user, hasPerm } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isDealerAdmin = user?.role === 'dealer_admin';
  const isPM = user?.role === 'producer_manager';
  const isDealerUser = user?.role === 'dealer_user';

  const [tab, setTab] = useState(isDealerAdmin || isPM || isDealerUser ? 'users' : 'dealers');
  const TABS = [
    ...(!isDealerAdmin && !isPM && !isDealerUser ? [
      { id: 'dealers',   label: 'Дилеры' },
      { id: 'companies', label: 'Компании' },
    ] : []),
    { id: 'users', label: (isPM || isDealerUser) ? 'Мой профиль' : 'Пользователи' },
  ];
  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-lg font-bold text-gray-100">Администрирование</h1>
        <p className="text-xs text-gray-600 mt-0.5">{(isPM || isDealerUser) ? 'Смена своего пароля и просмотр своих прав' : 'Управление дилерами, компаниями и пользователями'}</p>
      </div>
      <div className="card overflow-hidden">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="p-5">
          {tab === 'dealers'   && <DealersTab isSuperAdmin={isSuperAdmin} />}
          {tab === 'companies' && <CompaniesTab isSuperAdmin={isSuperAdmin} />}
          {tab === 'users'     && <UsersTab isSuperAdmin={isSuperAdmin} isDealerAdmin={isDealerAdmin} isPM={isPM} isDealerUser={isDealerUser} currentUser={user} hasPerm={hasPerm} />}
        </div>
      </div>
    </div>
  );
}

// ── ДИЛЕРЫ ────────────────────────────────────────────────────
function DealersTab({ isSuperAdmin }) {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name: '', short_name: '', inn: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/dealers'); setDealers(data); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: '', short_name: '', inn: '', notes: '' }); setModal('new'); };
  const openEdit = (d) => { setForm({ name: d.name, short_name: d.short_name || '', inn: d.inn || '', notes: d.notes || '', is_active: d.is_active }); setModal(d); };

  const save = async () => {
    if (!form.name) { toast.error('Укажите название'); return; }
    setSaving(true);
    try {
      if (modal === 'new') { await api.post('/admin/dealers', form); toast.success('Дилер добавлен'); }
      else { await api.patch('/admin/dealers/' + modal.id, form); toast.success('Сохранено'); }
      setModal(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteDealer = async (id) => {
    try { await api.delete('/admin/dealers/' + id); toast.success('Дилер удалён'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="space-y-4">
      {isSuperAdmin && <button className="btn-primary" onClick={openNew}><Plus size={14} /> Добавить дилера</button>}
      <div className="space-y-2">
        {dealers.length === 0 && <p className="text-sm text-gray-600">Нет дилеров</p>}
        {dealers.map(d => (
          <div key={d.id} className="p-4 bg-gray-800/60 border border-gray-800 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Building2 size={15} className="text-gray-500 shrink-0 mt-0.5" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-200">{d.name}</span>
                    {d.short_name && <span className="text-xs text-gray-500">({d.short_name})</span>}
                    {!d.is_active && <Badge className="bg-gray-800 text-gray-500 border-gray-700">Неактивен</Badge>}
                  </div>
                  {d.inn && <div className="text-xs text-gray-600 mt-0.5">ИНН {d.inn}</div>}
                  {d.notes && <div className="text-xs text-gray-600 italic mt-0.5">{d.notes}</div>}
                </div>
              </div>
              {isSuperAdmin && (
                <div className="flex gap-1">
                  <button className="btn-ghost p-1.5" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                  <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteConfirm(d)}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
            {d.companies?.length > 0 && (
              <div className="mt-3 pl-5 space-y-1">
                {d.companies.map(c => (
                  <div key={c.id} className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="text-gray-600">▸</span>
                    <span className="text-gray-300">{c.name}</span>
                    {c.short_name && <span className="text-gray-600">({c.short_name})</span>}
                    {c.dealer_pct > 0 && <span className="text-blue-400 font-medium">{c.dealer_pct}%</span>}
                    {c.inn && <span className="text-gray-600">ИНН {c.inn}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <Modal title={modal === 'new' ? 'Новый дилер' : 'Редактировать дилера'} onClose={() => setModal(null)} size="sm">
          <div className="space-y-3">
            <Field label="Название" required><input className="input" value={form.name} autoFocus onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Краткое название"><input className="input" value={form.short_name} onChange={e => setForm(f => ({ ...f, short_name: e.target.value }))} /></Field>
            <Field label="ИНН"><input className="input mono" value={form.inn} onChange={e => setForm(f => ({ ...f, inn: e.target.value }))} /></Field>
            <Field label="Примечания"><textarea className="input resize-none" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Field>
            {modal !== 'new' && (
              <Field label="Статус">
                <Select value={form.is_active === false ? 'false' : 'true'} onChange={v => setForm(f => ({ ...f, is_active: v === 'true' }))}
                  options={[{ value: 'true', label: 'Активен' }, { value: 'false', label: 'Неактивен' }]} />
              </Field>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : 'Сохранить'}</button>
            </div>
          </div>
        </Modal>
      )}
      {deleteConfirm && (
        <ConfirmModal message={'Удалить дилера "' + deleteConfirm.name + '"? Будут удалены все связанные компании и пользователи.'}
          onConfirm={() => deleteDealer(deleteConfirm.id)} onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}

// ── КОМПАНИИ ──────────────────────────────────────────────────
function CompaniesTab({ isSuperAdmin }) {
  const [companies, setCompanies] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ dealer_id: '', name: '', short_name: '', inn: '', bank_details: '', dealer_pct: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: d }] = await Promise.all([api.get('/admin/companies'), api.get('/admin/dealers')]);
      setCompanies(c); setDealers(d);
    } catch { }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ dealer_id: '', name: '', short_name: '', inn: '', bank_details: '', dealer_pct: 0 }); setModal({}); };
  const openEdit = (c) => { setForm({ dealer_id: c.dealer_id || '', name: c.name, short_name: c.short_name || '', inn: c.inn || '', bank_details: c.bank_details || '', dealer_pct: c.dealer_pct || 0 }); setModal(c); };

  const save = async () => {
    if (!form.name) { toast.error('Укажите название'); return; }
    setSaving(true);
    try {
      if (!modal.id) { await api.post('/admin/companies', form); toast.success('Компания добавлена'); }
      else { await api.patch('/admin/companies/' + modal.id, form); toast.success('Сохранено'); }
      setModal(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteCompany = async (id) => {
    try { await api.delete('/admin/companies/' + id); toast.success('Компания удалена'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  const myCompanies = companies.filter(c => !c.dealer_id);
  const byDealer = dealers.map(d => ({ dealer: d, list: companies.filter(c => c.dealer_id === d.id) })).filter(g => g.list.length);

  const CompanyRow = ({ c }) => (
    <div className="flex items-start justify-between p-3 bg-gray-800/60 border border-gray-800 rounded-lg">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-200">{c.name}</span>
          {c.short_name && <span className="text-xs text-gray-500">({c.short_name})</span>}
          {c.dealer_id && c.dealer_pct > 0 && (
            <span className="text-xs bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded border border-blue-800">{c.dealer_pct}% скидка</span>
          )}
        </div>
        {c.inn && <div className="text-xs text-gray-600">ИНН {c.inn}</div>}
        {c.bank_details && <div className="text-xs text-gray-700 truncate max-w-xs">{c.bank_details.slice(0, 60)}{c.bank_details.length > 60 ? '…' : ''}</div>}
      </div>
      {isSuperAdmin && (
        <div className="flex gap-1 shrink-0 ml-3">
          <button className="btn-ghost p-1.5" onClick={() => openEdit(c)}><Edit2 size={14} /></button>
          <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteConfirm(c)}><Trash2 size={14} /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {isSuperAdmin && <button className="btn-primary" onClick={openNew}><Plus size={14} /> Добавить компанию</button>}

      {myCompanies.length > 0 && (
        <div>
          <div className="section-title mb-2 flex items-center gap-1.5"><Briefcase size={13} /> Мои структуры (производитель)</div>
          <div className="space-y-1.5">{myCompanies.map(c => <CompanyRow key={c.id} c={c} />)}</div>
        </div>
      )}
      {byDealer.map(g => (
        <div key={g.dealer.id}>
          <div className="section-title mb-2 flex items-center gap-1.5"><Building2 size={13} /> {g.dealer.name}</div>
          <div className="space-y-1.5">{g.list.map(c => <CompanyRow key={c.id} c={c} />)}</div>
        </div>
      ))}
      {companies.length === 0 && <p className="text-sm text-gray-600">Нет компаний</p>}

      {modal && (
        <Modal title={modal.id ? 'Редактировать компанию' : 'Новая компания'} onClose={() => setModal(null)} size="sm">
          <div className="space-y-3">
            <Field label="Принадлежит">
              <Select value={form.dealer_id} onChange={v => setForm(f => ({ ...f, dealer_id: v }))}
                options={[{ value: '', label: 'Мои структуры (производитель)' }, ...dealers.map(d => ({ value: d.id, label: d.name }))]} />
            </Field>
            <Field label="Название" required><input className="input" value={form.name} autoFocus onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Краткое название"><input className="input" value={form.short_name} onChange={e => setForm(f => ({ ...f, short_name: e.target.value }))} /></Field>
            <Field label="ИНН"><input className="input mono" value={form.inn} onChange={e => setForm(f => ({ ...f, inn: e.target.value }))} /></Field>
            {form.dealer_id && (
              <Field label="Дилерский %" hint="Скидка от розничной цены">
                <input type="number" min="0" max="100" step="0.5" className="input" value={form.dealer_pct}
                  onChange={e => setForm(f => ({ ...f, dealer_pct: e.target.value }))} />
              </Field>
            )}
            <Field label="Банковские реквизиты">
              <textarea className="input resize-none" rows={3} value={form.bank_details}
                onChange={e => setForm(f => ({ ...f, bank_details: e.target.value }))}
                placeholder="Банк, р/с, к/с, БИК..." />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : 'Сохранить'}</button>
            </div>
          </div>
        </Modal>
      )}
      {deleteConfirm && (
        <ConfirmModal message={'Удалить компанию "' + deleteConfirm.name + '"?'}
          onConfirm={() => deleteCompany(deleteConfirm.id)} onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}

// ── ПОЛЬЗОВАТЕЛИ ──────────────────────────────────────────────
function UsersTab({ isSuperAdmin, isDealerAdmin, isPM, isDealerUser, currentUser, hasPerm }) {
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [permsModal, setPermsModal] = useState(null);
  const [form, setForm] = useState({ dealer_id: '', username: '', email: '', full_name: '', role: 'dealer_user', password: '' });
  const [saving, setSaving] = useState(false);
  const [perms, setPerms] = useState([]);

  const selfOnly = isPM || isDealerUser;
  const isEditingSelf = !!modal?.id && !selfOnly && Number(modal.id) === Number(currentUser?.id);

  const load = async () => {
    setLoading(true);
    try {
      if (selfOnly) {
        const { data: u } = await api.get('/admin/users');
        setUsers(u);
        setDealers([]);
      } else if (isDealerAdmin) {
        const { data: u } = await api.get('/admin/users');
        setUsers(u);
        setDealers(currentUser?.dealer_id ? [{ id: currentUser.dealer_id, name: currentUser.dealer_name || 'Дилер' }] : []);
      } else {
        const [{ data: u }, { data: d }] = await Promise.all([api.get('/admin/users'), api.get('/admin/dealers')]);
        setUsers(u);
        setDealers(d);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ dealer_id: '', username: '', email: '', full_name: '', role: 'dealer_user', password: '' }); setModal({}); };
  const openEdit = (u) => {
    if (selfOnly) {
      setForm({ dealer_id: u.dealer_id || '', username: u.username, email: u.email, full_name: u.full_name, role: u.role, password: '', is_active: u.is_active });
      setModal({ ...u, passwordOnly: true });
      return;
    }
    setForm({ dealer_id: u.dealer_id || '', username: u.username, email: u.email, full_name: u.full_name, role: u.role, password: '', is_active: u.is_active });
    setModal(u);
  };

  const openPerms = async (u) => {
    try { const { data } = await api.get('/admin/users/' + u.id + '/permissions'); setPerms(data); setPermsModal(u); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // Проверяем право: явное или по умолчанию роли
  const getPermState = (section, perm, targetRole) => {
    const explicit = perms.find(p => p.section === section && p.perm === perm);
    if (explicit) return explicit.granted ? 'granted' : 'denied';
    if (targetRole === 'dealer_admin') {
      return (DEALER_ADMIN_DEFAULTS[section] || []).includes(perm) ? 'default' : 'none';
    }
    return 'none';
  };

  const togglePerm = (section, perm) => {
    const existing = perms.find(p => p.section === section && p.perm === perm);
    if (existing) {
      if (existing.granted) {
        setPerms(ps => ps.map(p => p.section === section && p.perm === perm ? { ...p, granted: false } : p));
      } else {
        setPerms(ps => ps.filter(p => !(p.section === section && p.perm === perm)));
      }
    } else {
      setPerms(ps => [...ps, { section, perm, granted: true }]);
    }
  };

  const savePerms = async () => {
    setSaving(true);
    try { await api.put('/admin/users/' + permsModal.id + '/permissions', perms); toast.success('Права сохранены'); setPermsModal(null); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const saveUser = async () => {
    if (selfOnly) {
      if (!form.password) { toast.error('Укажите новый пароль'); return; }
      setSaving(true);
      try {
        await api.patch('/admin/users/' + modal.id, { password: form.password });
        toast.success('Пароль изменён');
        setModal(null); load();
      } catch (err) { toast.error(errMsg(err)); }
      finally { setSaving(false); }
      return;
    }

    if (isEditingSelf) {
      if (!form.password && !form.full_name && !form.email) { toast.error('Нет данных для сохранения'); return; }
      setSaving(true);
      try {
        await api.patch('/admin/users/' + modal.id, {
          full_name: form.full_name,
          email: form.email,
          password: form.password || undefined,
        });
        toast.success(form.password ? 'Пароль изменён' : 'Сохранено');
        setModal(null); load();
      } catch (err) { toast.error(errMsg(err)); }
      finally { setSaving(false); }
      return;
    }

    if (!form.full_name || !form.username || !form.email) { toast.error('Заполните обязательные поля'); return; }
    if (!modal.id && !form.password) { toast.error('Укажите пароль'); return; }
    setSaving(true);
    try {
      if (!modal.id) { await api.post('/admin/users', form); toast.success('Пользователь создан'); }
      else { await api.patch('/admin/users/' + modal.id, form); toast.success('Сохранено'); }
      setModal(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteUser = async (id) => {
    try { await api.delete('/admin/users/' + id); toast.success('Пользователь удалён'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  const selfOnlyUsers = users.filter(u => u.id === currentUser?.id);
  const producerUsers = selfOnly ? selfOnlyUsers : users.filter(u => !u.dealer_id);
  const byDealer = selfOnly ? [] : dealers.map(d => ({ dealer: d, list: users.filter(u => u.dealer_id === d.id) })).filter(g => g.list.length);

  const UserRow = ({ u }) => {
    const role = ROLES[u.role] || {};
    return (
      <div className="flex items-center justify-between p-3 bg-gray-800/60 border border-gray-800 rounded-lg">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-200">{u.full_name}</span>
            <Badge className={role.color}>{role.label}</Badge>
            {!u.is_active && <Badge className="bg-gray-800 text-gray-500 border-gray-700">Заблокирован</Badge>}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{u.username} · {u.email}</div>
          {u.last_login_at && <div className="text-xs text-gray-700 mt-0.5">Вход: {fmtDt(u.last_login_at)}</div>}
        </div>
        {(isSuperAdmin || isDealerAdmin || selfOnly) && (
          <div className="flex gap-1">
            {(isSuperAdmin || isDealerAdmin || (selfOnly && u.id === currentUser?.id)) && (u.role === 'dealer_user' || u.role === 'dealer_admin' || u.role === 'producer_manager') && (
              <button className="btn-ghost p-1.5 text-yellow-400 hover:text-yellow-300" title="Права" onClick={() => openPerms(u)}><Key size={14} /></button>
            )}
            {(!selfOnly || u.id === currentUser?.id) && <button className="btn-ghost p-1.5" title={selfOnly ? 'Сменить пароль' : 'Редактировать'} onClick={() => openEdit(u)}><Edit2 size={14} /></button>}
            {!selfOnly && Number(u.id) !== Number(currentUser?.id) && <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteConfirm(u)}><Trash2 size={14} /></button>}
          </div>
        )}
      </div>
    );
  };

  // Для dealer_admin — только те права которые он имеет сам
  const availablePerms = isDealerAdmin
    ? ALL_PERMISSIONS.filter(p => hasPerm(p.section, p.perm))
    : ALL_PERMISSIONS;

  // Группируем права по секции для отображения
  const permsBySection = availablePerms.reduce((acc, p) => {
    if (!acc[p.section]) acc[p.section] = [];
    acc[p.section].push(p);
    return acc;
  }, {});

  const sectionLabels = { orders: 'Заказы', repairs: 'Ремонт', supplies: 'Поставки', files: 'Файлы' };

  return (
    <div className="space-y-5">
      {(isSuperAdmin || isDealerAdmin) && !selfOnly && <button className="btn-primary" onClick={openNew}><Plus size={14} /> Добавить пользователя</button>}
      {producerUsers.length > 0 && (
        <div>
          <div className="section-title mb-2 flex items-center gap-1.5"><Users size={13} /> {selfOnly ? 'Мой профиль' : 'Производитель'}</div>
          <div className="space-y-1.5">{producerUsers.map(u => <UserRow key={u.id} u={u} />)}</div>
        </div>
      )}
      {byDealer.map(g => (
        <div key={g.dealer.id}>
          <div className="section-title mb-2 flex items-center gap-1.5"><Building2 size={13} /> {g.dealer.name}</div>
          <div className="space-y-1.5">{g.list.map(u => <UserRow key={u.id} u={u} />)}</div>
        </div>
      ))}
      {users.length === 0 && <p className="text-sm text-gray-600">Нет пользователей</p>}

      {/* Модалка пользователя */}
      {modal && (
        <Modal title={selfOnly ? 'Сменить пароль' : (modal.id ? 'Редактировать пользователя' : 'Новый пользователь')} onClose={() => setModal(null)} size="sm">
          <div className="space-y-3">
            {selfOnly ? (
              <>
                <div className="text-sm text-gray-400">{currentUser?.full_name || currentUser?.username}</div>
                <Field label="Новый пароль" required>
                  <input type="password" className="input" autoFocus value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </Field>
              </>
            ) : (
              <>
                {!isDealerAdmin && (
                  <Field label="Принадлежит">
                    <Select value={form.dealer_id} onChange={v => setForm(f => ({ ...f, dealer_id: v }))}
                      options={[{ value: '', label: 'Производитель' }, ...dealers.map(d => ({ value: d.id, label: d.name }))]} />
                  </Field>
                )}
                <Field label="Полное имя" required><input className="input" value={form.full_name} autoFocus onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></Field>
                <Field label="Логин" required><input className="input" value={form.username} readOnly={isEditingSelf} disabled={isEditingSelf} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></Field>
                <Field label="Email" required><input type="email" className="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Field>
                <Field label="Пароль" hint={modal.id ? 'Оставьте пустым чтобы не менять' : undefined}>
                  <input type="password" className="input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </Field>
                {!isEditingSelf && (<>
                <Field label="Роль">
                  <Select value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))}
                    options={isDealerAdmin
                      ? ROLE_OPTS.filter(r => ['dealer_user','dealer_admin'].includes(r.value))
                      : ROLE_OPTS} />
                </Field>
                {modal.id && (
                  <Field label="Статус">
                    <Select value={form.is_active === false ? 'false' : 'true'} onChange={v => setForm(f => ({ ...f, is_active: v === 'true' }))}
                      options={[{ value: 'true', label: 'Активен' }, { value: 'false', label: 'Заблокирован' }]} />
                  </Field>
                )}
                </>)}
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={saveUser} disabled={saving}>{saving ? <Spinner size={14} /> : (selfOnly ? 'Сменить пароль' : 'Сохранить')}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Модалка прав */}
      {permsModal && (
        <Modal title={'Права: ' + permsModal.full_name} onClose={() => setPermsModal(null)} size="md">
          <div className="mb-4 p-3 bg-gray-900 rounded-lg text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 mr-4"><span className="w-3 h-3 bg-blue-600 rounded inline-block"></span> Предоставлено явно</span>
            <span className="inline-flex items-center gap-1 mr-4"><span className="w-3 h-3 bg-gray-700 rounded inline-block border border-gray-500"></span> По умолчанию роли</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-red-900/50 rounded inline-block border border-red-700"></span> Запрещено явно</span>
          </div>
          <div className="space-y-4">
            {Object.entries(permsBySection).map(([section, sPerms]) => (
              <div key={section}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">{sectionLabels[section] || section}</div>
                <div className="space-y-1">
                  {sPerms.map(p => {
                    const state = getPermState(p.section, p.perm, permsModal.role);
                    return (
                      <label key={p.section + '.' + p.perm}
                        className={'flex items-center gap-3 p-2 rounded transition-colors ' +
                          (selfOnly ? 'cursor-default ' : 'cursor-pointer ') +
                          (state === 'granted' ? 'hover:bg-blue-900/20' :
                           state === 'denied'  ? 'hover:bg-red-900/10' : 'hover:bg-gray-800')}
                        onClick={() => { if (!selfOnly) togglePerm(p.section, p.perm); }}>
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors
                          ${state === 'granted' ? 'bg-blue-600 border-blue-600' :
                            state === 'denied'  ? 'bg-red-900/50 border-red-700' :
                            state === 'default' ? 'bg-gray-700 border-gray-500' : 'border-gray-600'}`}>
                          {state === 'granted' && <Check size={11} className="text-white" />}
                          {state === 'denied'  && <Minus size={11} className="text-red-400" />}
                          {state === 'default' && <Check size={11} className="text-gray-400" />}
                        </div>
                        <span className="text-sm text-gray-300 flex-1">{p.label}</span>
                        {state === 'default' && <span className="text-xs text-gray-600">по роли</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-800">
            <button className="btn-secondary" onClick={() => setPermsModal(null)}>{selfOnly ? 'Закрыть' : 'Отмена'}</button>
            {!selfOnly && <button className="btn-primary" onClick={savePerms} disabled={saving}>{saving ? <Spinner size={14} /> : 'Сохранить права'}</button>}
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <ConfirmModal message={'Удалить пользователя "' + deleteConfirm.full_name + '"?'}
          onConfirm={() => deleteUser(deleteConfirm.id)} onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}
